/**
 * usePeerContext — fetches peer benchmarks for a portfolio-scoped widget.
 *
 * Calls `/api/govdata/peer-context/?portfolio={id}&fiscal_year={range}` and
 * formats the response as BenchmarkDataSeries[] so it slots directly into
 * the existing groupComparisons rendering path on Bar / Line renderers.
 *
 * Two series are returned (when available):
 *   - "All DoW" (always)
 *   - "{parent_sub_agency_name}" (when the portfolio has a clear parent)
 *
 * Phase H1 of issue #90 v3 plan.
 */
import { useEffect, useState } from 'react'
import { api } from '../../../services/api'
import type { PerfRow } from './types'
import type { BenchmarkDataSeries } from './useBenchmarkQueries'

interface PeerContextResponse {
  parent_label: string | null
  parent_sub_agency_code: string | null
  series: {
    all_dod: PerfRow[]
    parent_sub_agency: PerfRow[]
  }
}

export function usePeerContext(
  portfolioGroupId: string | undefined,
  fiscalYear: number | number[] | string | undefined,
  enabled: boolean,
): BenchmarkDataSeries[] {
  const [series, setSeries] = useState<BenchmarkDataSeries[]>([])

  // Stable key for re-fetch — only rerun if any of these actually change.
  const fyKey = Array.isArray(fiscalYear) ? fiscalYear.join(',') : String(fiscalYear ?? '')

  useEffect(() => {
    if (!enabled || !portfolioGroupId) {
      setSeries([])
      return
    }
    const params: Record<string, string> = { portfolio: portfolioGroupId }
    if (fyKey) params.fiscal_year = fyKey

    let cancelled = false
    api
      .getRaw<PeerContextResponse>('/api/govdata/peer-context/', params)
      .then((resp) => {
        if (cancelled) return
        const out: BenchmarkDataSeries[] = []
        if (resp.series?.all_dod?.length > 0) {
          out.push({
            source: {
              id: 'peer-all-dod',
              type: 'all_dod',
              entity_code: '',
              label: 'All DoW',
              color: '#8b5cf6',
            } as any,
            data: resp.series.all_dod,
            loading: false,
            error: null,
          })
        }
        if (resp.parent_label && resp.series?.parent_sub_agency?.length > 0) {
          out.push({
            source: {
              id: 'peer-parent-sub-agency',
              type: 'sub_agency',
              entity_code: resp.parent_sub_agency_code ?? '',
              label: resp.parent_label,
              color: '#06b6d4',
            } as any,
            data: resp.series.parent_sub_agency,
            loading: false,
            error: null,
          })
        }
        setSeries(out)
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('peer-context fetch failed:', err)
        setSeries([])
      })

    return () => { cancelled = true }
  }, [portfolioGroupId, fyKey, enabled])

  return series
}
